import { useEffect, useState } from "react";
import { Appointments as ApptAPI, Prospects, API } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar as CalendarView } from "@/components/ui/calendar";
import { toast } from "sonner";
import { Plus, Trash, GoogleLogo, MicrosoftOutlookLogo, AppleLogo, DownloadSimple, Clock, MapPin } from "@phosphor-icons/react";

export default function Appointments() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = () => ApptAPI.list().then(setList);
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Delete appointment?")) return;
    await ApptAPI.remove(id);
    load();
  };

  const upcoming = list.filter(a => a.starts_at >= new Date().toISOString());
  const past = list.filter(a => a.starts_at < new Date().toISOString());

  return (
    <div data-testid="appointments-page">
      <PageHeader
        overline="Meetings · Bookings"
        title="Appointments"
        description="Booked calls & meetings. Add to Google, Outlook, Apple, or download .ics."
        testId="appointments-header"
        actions={
          <Button onClick={() => setOpen(true)} className="bg-neutral-900 hover:bg-black text-white" data-testid="new-appointment-btn">
            <Plus size={16} className="mr-2" /> Schedule
          </Button>
        }
      />

      <div className="p-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div>
            <div className="overline mb-3">Upcoming</div>
            <div className="space-y-3 stagger-in">
              {upcoming.length === 0 && <div className="text-sm text-neutral-500 py-6">No upcoming meetings.</div>}
              {upcoming.map(a => (
                <ApptCard key={a.id} a={a} onSelect={() => setSelected(a)} onDelete={() => remove(a.id)} />
              ))}
            </div>
          </div>

          {past.length > 0 && (
            <div>
              <div className="overline mb-3">Past</div>
              <div className="space-y-3 opacity-70">
                {past.map(a => <ApptCard key={a.id} a={a} onSelect={() => setSelected(a)} onDelete={() => remove(a.id)} />)}
              </div>
            </div>
          )}
        </div>

        <Card className="p-4 rounded-md border-neutral-200 h-fit sticky top-6" data-testid="calendar-view">
          <div className="overline mb-2 px-2">Calendar</div>
          <CalendarView
            mode="single"
            selected={selected ? new Date(selected.starts_at) : undefined}
            className="rounded-md"
          />
          {selected && (
            <div className="mt-4 p-3 border-t border-neutral-200">
              <div className="text-sm font-medium">{selected.title}</div>
              <div className="text-xs text-neutral-500 font-mono mt-1">with {selected.prospect_name}</div>
              <div className="text-xs text-neutral-500 mt-1">{new Date(selected.starts_at).toLocaleString()}</div>
              <CalendarLinks a={selected} />
            </div>
          )}
        </Card>
      </div>

      <NewApptDialog open={open} onOpenChange={setOpen} onSaved={load} />
    </div>
  );
}

function ApptCard({ a, onSelect, onDelete }) {
  const start = new Date(a.starts_at);
  const end = new Date(a.ends_at);
  return (
    <Card className="p-5 rounded-md border-neutral-200 hover:border-neutral-900 transition-colors cursor-pointer" onClick={onSelect} data-testid={`appt-${a.id}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">
            {start.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
          </div>
          <div className="font-display text-2xl mt-1">{a.title}</div>
          <div className="text-sm text-neutral-600 mt-1">with {a.prospect_name}</div>
          <div className="flex items-center gap-4 mt-3 text-xs text-neutral-500 font-mono">
            <span className="flex items-center gap-1"><Clock size={12} />{start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} → {end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
            {a.location && <span className="flex items-center gap-1"><MapPin size={12} />{a.location}</span>}
          </div>
        </div>
        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onDelete(); }} data-testid={`delete-appt-${a.id}`}>
          <Trash size={14} />
        </Button>
      </div>
      <CalendarLinks a={a} />
    </Card>
  );
}

function CalendarLinks({ a }) {
  const links = a.calendar_links || {};
  const gcal = links.google || `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(a.title)}`;
  const outlook = links.outlook;
  const office = links.office365;
  const ics = `${API}/appointments/${a.id}/ics`;

  useEffect(() => {}, []);
  return (
    <div className="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t border-neutral-100">
      <span className="overline mr-1">Add to</span>
      <a href={gcal} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-neutral-300 rounded hover:bg-neutral-900 hover:text-white transition-colors" data-testid={`google-${a.id}`}><GoogleLogo size={12} /> Google</a>
      {outlook && <a href={outlook} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-neutral-300 rounded hover:bg-neutral-900 hover:text-white transition-colors" data-testid={`outlook-${a.id}`}><MicrosoftOutlookLogo size={12} /> Outlook</a>}
      {office && <a href={office} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-neutral-300 rounded hover:bg-neutral-900 hover:text-white transition-colors" data-testid={`office365-${a.id}`}>Office 365</a>}
      <a href={ics} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-neutral-300 rounded hover:bg-neutral-900 hover:text-white transition-colors" data-testid={`ics-${a.id}`}><AppleLogo size={12} /> Apple / .ics</a>
    </div>
  );
}

function NewApptDialog({ open, onOpenChange, onSaved }) {
  const [prospects, setProspects] = useState([]);
  const [f, setF] = useState({
    prospect_id: "",
    prospect_name: "",
    prospect_email: "",
    title: "Discovery Call",
    description: "",
    date: new Date().toISOString().slice(0, 10),
    time: "10:00",
    duration_min: 30,
    location: "Video call",
  });

  useEffect(() => {
    if (open) Prospects.list().then(setProspects);
  }, [open]);

  const onProspectChange = (id) => {
    const p = prospects.find(x => x.id === id);
    if (p) setF({ ...f, prospect_id: id, prospect_name: p.business_name, prospect_email: p.email || "" });
  };

  const submit = async () => {
    if (!f.prospect_name) return toast.error("Prospect name required");
    const starts = new Date(`${f.date}T${f.time}:00`);
    const ends = new Date(starts.getTime() + f.duration_min * 60 * 1000);
    try {
      await ApptAPI.create({
        prospect_id: f.prospect_id || null,
        prospect_name: f.prospect_name,
        prospect_email: f.prospect_email || null,
        title: f.title,
        description: f.description,
        starts_at: starts.toISOString(),
        ends_at: ends.toISOString(),
        location: f.location,
      });
      toast.success("Appointment scheduled");
      onOpenChange(false);
      onSaved();
    } catch { toast.error("Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="new-appt-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Schedule appointment</DialogTitle>
          <DialogDescription>Send confirmation with calendar links to the prospect's email.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Prospect</Label>
            <Select value={f.prospect_id} onValueChange={onProspectChange}>
              <SelectTrigger data-testid="appt-prospect"><SelectValue placeholder="Select existing prospect or enter manually below" /></SelectTrigger>
              <SelectContent>
                {prospects.map(p => <SelectItem key={p.id} value={p.id}>{p.business_name} — {p.contact_name || "—"}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Name</Label><Input value={f.prospect_name} onChange={e => setF({...f, prospect_name: e.target.value})} data-testid="appt-name" /></div>
            <div><Label>Email (for confirmation)</Label><Input value={f.prospect_email} onChange={e => setF({...f, prospect_email: e.target.value})} data-testid="appt-email" /></div>
          </div>
          <div><Label>Title</Label><Input value={f.title} onChange={e => setF({...f, title: e.target.value})} data-testid="appt-title" /></div>
          <div className="grid grid-cols-3 gap-4">
            <div><Label>Date</Label><Input type="date" value={f.date} onChange={e => setF({...f, date: e.target.value})} data-testid="appt-date" /></div>
            <div><Label>Time</Label><Input type="time" value={f.time} onChange={e => setF({...f, time: e.target.value})} data-testid="appt-time" /></div>
            <div><Label>Duration (min)</Label><Input type="number" value={f.duration_min} onChange={e => setF({...f, duration_min: Number(e.target.value)})} data-testid="appt-duration" /></div>
          </div>
          <div><Label>Location</Label><Input value={f.location} onChange={e => setF({...f, location: e.target.value})} data-testid="appt-location" /></div>
          <div><Label>Notes</Label><Textarea rows={2} value={f.description} onChange={e => setF({...f, description: e.target.value})} data-testid="appt-description" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-neutral-900 hover:bg-black" onClick={submit} data-testid="save-appt-btn">Schedule</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
